import { Head, Link, usePage } from "@inertiajs/react"
import { Star } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  STATUS_LABELS,
  STATUS_TONES,
  type ProjectStatus,
} from "@/lib/project-status"
import { relative } from "@/lib/relative-time"
import type { PageProps } from "@/types/inertia"

type RecentRow = {
  id: number
  display_name: string
  plan: string
  customer: string
  status: ProjectStatus
  visited_at: string
}

type FavoriteRow = {
  type: "Community" | "Plan" | "Project"
  id: number
  name: string
  sub: string
  meta: string | null
  url: string
}

type Props = {
  stats: { takeoffs_created: number; projects_sent: number; waiting: number }
  recent: RecentRow[]
  favorites: FavoriteRow[]
}

export default function Dashboard({ stats, recent, favorites }: Props) {
  const { props } = usePage<PageProps>()
  const first = (props.current_user?.name ?? "").split(" ")[0]
  const hour = new Date().getHours()
  const daypart = hour < 12 ? "morning" : hour < 17 ? "afternoon" : "evening"
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  })

  return (
    <>
      <Head title="Home">
        <meta name="description" content="Your week at a glance — recent projects, favorites, and what's waiting on you." />
        <meta property="og:title" content="Home" />
        <meta property="og:description" content="Your week at a glance — recent projects, favorites, and what's waiting on you." />
      </Head>
      <AppShell wide>
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-hairline pb-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{today}</p>
            <h1 className="mt-1">
              Good {daypart}
              {first ? `, ${first}` : ""}
            </h1>
          </div>
          <Button asChild>
            <Link href="/projects/new">New project</Link>
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(
            [
              ["Takeoffs created", stats.takeoffs_created, "this week"],
              ["Projects sent", stats.projects_sent, "this week"],
              ["Waiting on you", stats.waiting, "measured, not sent"],
            ] as const
          ).map(([label, value, note]) => (
            <div key={label} className="rounded-lg border border-hairline bg-page px-4 py-3.5">
              <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{label}</p>
              <p className="mt-1.5 flex items-baseline gap-2">
                <span className="font-mono text-2xl font-semibold text-ink-display">{value}</span>
                <span className="text-sm text-ink-muted">{note}</span>
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-wrap items-start gap-6">
          <div className="min-w-0 flex-[2_1_540px] overflow-hidden rounded-lg border border-hairline bg-page">
            <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                Recent projects
              </h2>
              <Link href="/projects" className="text-sm">All projects</Link>
            </div>
            {recent.length === 0 ? (
              <p className="px-4 py-6 text-sm text-ink-muted">
                Nothing yet — projects you open will land here.
              </p>
            ) : (
              <ul className="divide-y divide-hairline">
                {recent.map((row) => (
                  <li key={row.id}>
                    <Link
                      href={`/projects/${row.id}`}
                      className="grid grid-cols-[minmax(130px,1.2fr)_minmax(140px,1.6fr)_auto_86px] items-center gap-x-3 px-4 py-2.5 text-sm text-ink-body no-underline hover:bg-surface"
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink-display">
                          {row.display_name}
                        </span>
                        <span className="block truncate text-xs text-ink-muted">{row.plan}</span>
                      </span>
                      <span className="truncate text-ink-muted">{row.customer}</span>
                      <Badge tone={STATUS_TONES[row.status]}>{STATUS_LABELS[row.status]}</Badge>
                      <span className="text-right text-xs text-ink-muted">
                        {relative(row.visited_at, { suffix: true })}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="min-w-0 flex-[1_1_300px] overflow-hidden rounded-lg border border-hairline bg-page">
            <div className="flex items-center justify-between border-b border-hairline px-4 py-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                Favorites
              </h2>
              <span className="text-xs text-ink-muted">{favorites.length} pinned</span>
            </div>
            <ul className="divide-y divide-hairline">
              {favorites.map((fav) => (
                <li key={`${fav.type}-${fav.id}`}>
                  <Link
                    href={fav.url}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink-body no-underline hover:bg-surface"
                  >
                    <Star className="h-3.5 w-3.5 shrink-0 fill-accent stroke-accent" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-ink-display">{fav.name}</span>
                      <span className="block truncate text-xs text-ink-muted">{fav.sub}</span>
                    </span>
                    {fav.meta && <span className="shrink-0 text-xs text-ink-muted">{fav.meta}</span>}
                  </Link>
                </li>
              ))}
            </ul>
            <p className="px-4 py-3 text-xs text-ink-muted">
              Star a community, plan, or project from its page to keep it here.
            </p>
          </div>
        </div>
      </AppShell>
    </>
  )
}
